import {
  makeStyles,
  Button,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import {
  getChatControlData,
  setChatDialogOpen,
} from 'components/ChatDialog/store/slices/chatSlice';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import TextInput from 'components/TextInput/TextInput';
import { minimalScrollbar } from 'components/theme/scrollbars';
import ChatProjectListItem from './ChatProjectListItem';
import { selectProjects } from 'store/projects/selectors/projectsSelectors';
import { selectUserActiveEmployerId } from 'store/user/selectors.js/user.selectors';
import { loadChatUsers } from 'components/ChatDialog/store/thunks/loadChatUsers';
import { setThreadWithUsers } from 'components/ChatDialog/store/thunks/loadThreadUsers';
import { getProjectEmailData } from 'store/projectEmail/projectEmailSlice';
import { selectIsRequiredPermissionForProject } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { PROJECT_EMAIL_READ } from 'scenes/Permissions/constants/permissions';
import isFeatureEnabled, { Feature } from 'utils/isFeatureEnabled';

// The message center lists what is still waiting for the user, not an archive
// of everything ever said. An entry stays only while it has unread messages
// that arrived inside this window: opening it zeroes the unread count so it
// leaves at once, and anything older ages out by itself. Nothing is lost:
// what ages out stays reachable through the search box, and the banner above
// the list offers to bring it straight back.
const UNREAD_WINDOW_DAYS = 14;

const unreadWindowCutoff = () =>
  moment().subtract(UNREAD_WINDOW_DAYS, 'days').toISOString();

// A null cutoff drops the age test — that is how the banner reveals what it
// counted. Timestamps are ISO-8601 UTC from the API, so they compare as
// strings, the same assumption the sorting below makes. A missing one is a
// data gap rather than an old message, and hiding a genuinely unread message
// is the worse failure, so treat it as fresh.
const isFreshUnread = (unreadCount, lastMessageAt, cutoff) =>
  unreadCount > 0 && (!cutoff || !lastMessageAt || lastMessageAt >= cutoff);

// Mirrors showEmailButton in the list below: the thread rows that draw their
// project's email badge.
const drawsProjectEmail = (thread, project, permissions) =>
  !thread.isSubthread &&
  !thread.directRecipient &&
  isFeatureEnabled(Feature.PROJECT_EMAIL, project) &&
  !!permissions[project.id];

const useStyles = makeStyles((theme) => ({
  root: {
    // Same surface as the timer drawer paper (TimerButton.js) so the
    // projectsListButton pills stay visible on every theme — with
    // dialogPaperFullscreen the classic theme painted pills and drawer
    // in the same color.
    backgroundColor: theme.palette.background.default,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    // Same width as the timer drawer paper (TimerButton.js drawerPaper).
    width: '85vw',
    maxWidth: 360,
  },
  // Header row copied from the timer drawer (TimerContent.js), with the
  // timer drawer paper's outer paddings folded in.
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 1, 1, 1),
  },
  drawerTitle: {
    ...theme.typography.fontPanton,
    fontSize: '0.95rem',
  },
  drawerCloseButton: {
    ...theme.component.projectsListButton,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.75),
    minWidth: theme.spacing(4),
    minHeight: theme.spacing(4),
  },
  noMessages: {
    textAlign: 'center',
    padding: theme.spacing(4, 2),
    color: theme.palette.text.secondary,
    fontSize: '2rem',
  },
  noMessagesHint: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.85rem',
  },
  // Sits above the list in the same panel style as the search box, so the
  // offer reads as drawer chrome rather than as another conversation row.
  olderUnread: {
    ...theme.component.dashboardPaper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1, 1, 1.5),
    marginBottom: theme.spacing(1),
  },
  olderUnreadText: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  // Same pill as the drawer's close button (drawerCloseButton).
  olderUnreadButton: {
    ...theme.component.projectsListButton,
    borderRadius: theme.spacing(0.5),
    flexShrink: 0,
  },
  // Deliberately not sticky. Each project group is wrapped in a Fragment, which
  // emits no element to scope a sticky header to, so every header shared the
  // scroll container as its containing block and they all pinned to top: 0 at
  // once, piling up over each other instead of handing off. A project rarely
  // carries enough threads for a sticky header to be worth a wrapper.
  projectHeader: {
    padding: theme.spacing(1, 0),
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
  },
  divider: {
    marginBottom: theme.spacing(1),
  },
  // Same panel as the one around the Projects-page search
  // (ProjectsActions.js root: dashboardPaper), without the filter/join
  // buttons. Side margins keep the drawer gutter; stays fixed while
  // only the list scrolls.
  searchContainer: {
    ...theme.component.dashboardPaper,
    padding: theme.spacing(2),
    margin: theme.spacing(0, 1, 2, 1),
    flexShrink: 0,
  },
  listContainer: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    padding: theme.spacing(0, 1),
    ...minimalScrollbar(theme),
  },
}));

const ChatMenuContent = ({ onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { threads, allUsers } = useSelector(getChatControlData);
  const allProjects = useSelector(selectProjects);
  const activeEmployerId = useSelector(selectUserActiveEmployerId);

  const projects = React.useMemo(() => {
    if (!activeEmployerId) return allProjects;
    return Object.fromEntries(
      Object.entries(allProjects).filter(
        ([, project]) =>
          project.masterParent === activeEmployerId ||
          project.id === activeEmployerId
      )
    );
  }, [allProjects, activeEmployerId]);

  const {
    unreadMessages,
    emailMessagesCounts,
    emailLastMessageAt = {},
  } = useSelector(getProjectEmailData);

  // Every project whose email this user may read. Derived from the full project
  // list rather than from the filtered one, because the filters below now
  // consult it — the other way round would be circular.
  const emailEnabledProjects = React.useMemo(
    () =>
      Object.values(projects).filter((project) =>
        isFeatureEnabled(Feature.PROJECT_EMAIL, project)
      ),
    [projects]
  );

  const projectEmailPermissions = useSelector((state) => {
    const permissions = {};
    emailEnabledProjects.forEach((project) => {
      permissions[project.id] = selectIsRequiredPermissionForProject(state, {
        projectId: project.id,
        permission: PROJECT_EMAIL_READ,
      });
    });
    return permissions;
  });

  // Only employer projects get an email row of their own; every other project's
  // email badge rides on its chat thread row.
  const employerProjectsWithEmail = React.useMemo(
    () =>
      emailEnabledProjects.filter(
        (project) =>
          project.type === 'EMPLOYER' && projectEmailPermissions[project.id]
      ),
    [emailEnabledProjects, projectEmailPermissions]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [showOlderUnread, setShowOlderUnread] = useState(false);

  const { t, i18n } = useTranslation('common');

  const handleChatButtonClick = React.useCallback(
    (thread) => {
      // Set the thread and load its users
      dispatch(setThreadWithUsers({ thread }));

      // Open the dialog
      dispatch(setChatDialogOpen(true));

      // Close the chat menu so it isn't left open in front of the dialog
      if (onClose) {
        onClose();
      }
    },
    [dispatch, onClose]
  );

  const collator = React.useMemo(() => {
    try {
      return new Intl.Collator(i18n.language);
    } catch (e) {
      return new Intl.Collator('en');
    }
  }, [i18n.language]);

  const sortAlphabetically = React.useCallback(
    (a, b) => {
      const lowerA = (a || '').toLowerCase();
      const lowerB = (b || '').toLowerCase();
      try {
        return collator.compare(lowerA, lowerB);
      } catch (e) {
        return lowerA.localeCompare(lowerB);
      }
    },
    [collator]
  );

  // Memoize filtering of threads by search query
  const filteredThreadsByProject = React.useMemo(() => {
    const cutoff = showOlderUnread ? null : unreadWindowCutoff();
    return Object.values(threads).reduce((acc, thread) => {
      const projectId = thread.projectId;
      const project = projects[projectId];
      if (!projectId || !project) {
        return acc;
      }
      const projectName = (project.name || '').toLowerCase();
      const threadTitle = (thread.title || '').toLowerCase();
      const query = searchQuery.toLowerCase();
      if (!query) {
        // A row earns its place on its own unread chat, or on its project's
        // unread email — for anything but an employer project that row is the
        // only place the email badge is drawn, so filtering on chat alone hid
        // those messages outright. Employer projects are left out here because
        // they get an email row of their own below, and rescuing the thread
        // too would draw the same badge twice.
        const isWaiting =
          isFreshUnread(
            thread.unreadMessagesCount,
            thread.lastMessageAt,
            cutoff
          ) ||
          (project.type !== 'EMPLOYER' &&
            drawsProjectEmail(thread, project, projectEmailPermissions) &&
            isFreshUnread(
              unreadMessages[project.projectKey],
              emailLastMessageAt[project.projectKey],
              cutoff
            ));
        if (!isWaiting) return acc;
        if (!acc[projectId]) acc[projectId] = [];
        acc[projectId].push(thread);
        return acc;
      }
      const projectMatches = projectName.includes(query);
      const threadMatches = threadTitle.includes(query);
      if (projectMatches) {
        if (!acc[projectId]) acc[projectId] = [];
        acc[projectId].push(thread);
        return acc;
      }
      if (threadMatches) {
        if (!acc[projectId]) acc[projectId] = [];
        acc[projectId].push(thread);
        return acc;
      }
      return acc;
    }, {});
  }, [
    threads,
    projects,
    searchQuery,
    showOlderUnread,
    projectEmailPermissions,
    unreadMessages,
    emailLastMessageAt,
  ]);

  // Memoize sorting of threads within each project
  const threadsByProject = React.useMemo(() => {
    const grouped = { ...filteredThreadsByProject };
    // Sort threads within each project
    Object.keys(grouped).forEach((projectId) => {
      grouped[projectId].sort((a, b) => {
        // Thread type priority: 0 = main project thread, 1 = private/direct, 2 = subthread
        const typeOf = (t) => {
          if (t.isSubthread) return 2;
          if (t.directRecipient) return 1;
          return 0;
        };
        const aType = typeOf(a);
        const bType = typeOf(b);
        if (aType !== bType) return aType - bType;
        // Threads with messages before threads without messages
        if (a.hasMessages && !b.hasMessages) return -1;
        if (!a.hasMessages && b.hasMessages) return 1;
        // Both have no messages: alphabetical
        if (!a.hasMessages && !b.hasMessages)
          return sortAlphabetically(a.title, b.title);
        // Both have messages: unread tier first
        const aUnread = a.unreadMessagesCount > 0;
        const bUnread = b.unreadMessagesCount > 0;
        if (aUnread && !bUnread) return -1;
        if (!aUnread && bUnread) return 1;
        // Same tier: sort by lastMessageAt DESC
        const aTs = a.lastMessageAt || '';
        const bTs = b.lastMessageAt || '';
        if (aTs > bTs) return -1;
        if (aTs < bTs) return 1;
        return sortAlphabetically(a.title, b.title);
      });
    });
    return grouped;
  }, [filteredThreadsByProject, sortAlphabetically]);

  // Filter employer projects by search query
  const filteredEmployerProjects = React.useMemo(() => {
    if (!searchQuery) {
      const cutoff = showOlderUnread ? null : unreadWindowCutoff();
      return employerProjectsWithEmail.filter((project) =>
        isFreshUnread(
          unreadMessages[project.projectKey],
          emailLastMessageAt[project.projectKey],
          cutoff
        )
      );
    }
    const query = searchQuery.toLowerCase();
    return employerProjectsWithEmail.filter((project) => {
      const projectName = (project?.name || '').toLowerCase();
      return projectName.includes(query);
    });
  }, [
    employerProjectsWithEmail,
    searchQuery,
    unreadMessages,
    emailLastMessageAt,
    showOlderUnread,
  ]);

  // How many unread messages the age cut-off is holding back. Walks the same
  // two sources the filters above do, so the tally is exactly what Show puts on
  // screen and never promises messages no row can display: chat per thread,
  // email once per project. Counted from the cut-off whatever the toggle says,
  // so revealing flips the banner's action rather than making it vanish.
  const hiddenOlderUnreadCount = React.useMemo(() => {
    const cutoff = unreadWindowCutoff();
    const isHidden = (unreadCount, lastMessageAt) =>
      unreadCount > 0 && !isFreshUnread(unreadCount, lastMessageAt, cutoff);

    const countedProjects = new Set();
    const hiddenEmailOf = (project) => {
      if (countedProjects.has(project.id)) return 0;
      countedProjects.add(project.id);
      const unreadCount = unreadMessages[project.projectKey];
      return isHidden(unreadCount, emailLastMessageAt[project.projectKey])
        ? unreadCount
        : 0;
    };

    let hidden = 0;
    for (const thread of Object.values(threads)) {
      const project = projects[thread.projectId];
      if (!project) continue;
      if (isHidden(thread.unreadMessagesCount, thread.lastMessageAt)) {
        hidden += thread.unreadMessagesCount;
      }
      if (
        project.type !== 'EMPLOYER' &&
        drawsProjectEmail(thread, project, projectEmailPermissions)
      ) {
        hidden += hiddenEmailOf(project);
      }
    }
    for (const project of employerProjectsWithEmail) {
      hidden += hiddenEmailOf(project);
    }
    return hidden;
  }, [
    threads,
    projects,
    employerProjectsWithEmail,
    projectEmailPermissions,
    unreadMessages,
    emailLastMessageAt,
  ]);

  const sortedProjectIds = React.useMemo(() => {
    // Get project IDs from threadsByProject
    const projectIdsFromThreads = Object.keys(threadsByProject);

    // Get employer project IDs (these don't have threads)
    const employerProjectIds = filteredEmployerProjects.map(
      (project) => project.id
    );

    // Combine all project IDs
    const allProjectIds = [...projectIdsFromThreads, ...employerProjectIds];

    // Pre-compute latest message timestamp per project for efficient sorting
    const latestTs = {};
    for (const id of allProjectIds) {
      const projectKey = projects[id]?.projectKey;
      const tsList = [
        ...(threadsByProject[id] || [])
          .map((t) => t.lastMessageAt)
          .filter(Boolean),
        emailLastMessageAt[projectKey] || null,
      ].filter(Boolean);
      latestTs[id] =
        tsList.length > 0
          ? tsList.reduce((max, ts) => (ts > max ? ts : max), '')
          : null;
    }

    // Sort all projects together
    return allProjectIds.sort((a, b) => {
      const aIsEmployer = employerProjectIds.includes(a);
      const bIsEmployer = employerProjectIds.includes(b);

      // Check for unread messages
      const aHasUnread = aIsEmployer
        ? unreadMessages[projects[a]?.projectKey] > 0
        : threadsByProject[a]?.some(
            (thread) => thread.unreadMessagesCount > 0
          ) || unreadMessages[projects[a]?.projectKey] > 0;
      const bHasUnread = bIsEmployer
        ? unreadMessages[projects[b]?.projectKey] > 0
        : threadsByProject[b]?.some(
            (thread) => thread.unreadMessagesCount > 0
          ) || unreadMessages[projects[b]?.projectKey] > 0;

      // Check for any messages (read or unread)
      const aHasMessages = aIsEmployer
        ? emailMessagesCounts[projects[a]?.projectKey] > 0
        : threadsByProject[a]?.some((t) => t.hasMessages) ||
          emailMessagesCounts[projects[a]?.projectKey] > 0;
      const bHasMessages = bIsEmployer
        ? emailMessagesCounts[projects[b]?.projectKey] > 0
        : threadsByProject[b]?.some((t) => t.hasMessages) ||
          emailMessagesCounts[projects[b]?.projectKey] > 0;

      // Tier 1: projects with unread messages first
      if (aHasUnread && !bHasUnread) return -1;
      if (!aHasUnread && bHasUnread) return 1;

      // Within same unread tier: sort by latest message timestamp DESC
      if (aHasUnread && bHasUnread) {
        const aTs = latestTs[a] || '';
        const bTs = latestTs[b] || '';
        if (aTs > bTs) return -1;
        if (aTs < bTs) return 1;
        return sortAlphabetically(projects[a]?.name, projects[b]?.name);
      }

      // Tier 2: projects with any messages before those without
      if (aHasMessages && !bHasMessages) return -1;
      if (!aHasMessages && bHasMessages) return 1;

      // Both have messages (no unread): sort by latest message timestamp DESC
      if (aHasMessages && bHasMessages) {
        const aTs = latestTs[a] || '';
        const bTs = latestTs[b] || '';
        if (aTs > bTs) return -1;
        if (aTs < bTs) return 1;
        return sortAlphabetically(projects[a]?.name, projects[b]?.name);
      }

      // Tier 3: projects without messages (only when search is active) — alphabetical
      return sortAlphabetically(projects[a]?.name, projects[b]?.name);
    });
  }, [
    threadsByProject,
    projects,
    filteredEmployerProjects,
    unreadMessages,
    emailMessagesCounts,
    emailLastMessageAt,
    sortAlphabetically,
  ]);

  // Find private threads where directRecipient user data is missing and load them
  useEffect(() => {
    const directRecipientIdsToLoad = [];
    Object.values(threads).forEach((thread) => {
      if (thread.directRecipient && !allUsers[thread.directRecipient]) {
        directRecipientIdsToLoad.push(thread.directRecipient);
      }
    });
    if (directRecipientIdsToLoad.length > 0) {
      dispatch(
        loadChatUsers({
          userIds: directRecipientIdsToLoad,
          addToCurrentUsers: false,
        })
      );
    }
  }, [threads, allUsers, dispatch]);

  // Memoized render function for thread items
  const renderThreadItem = React.useCallback(
    (
      thread,
      projectKey,
      projectName,
      projectEmailEnabled,
      hasPermission,
      unreadEmailCount
    ) => {
      const directRecipient = thread.directRecipient
        ? allUsers[thread.directRecipient]
        : null;
      return (
        <ChatProjectListItem
          key={thread.id}
          thread={thread}
          title={thread.title}
          subtitle={projectName}
          lastMessageAt={thread.lastMessageAt}
          avatarSrc={directRecipient?.image}
          showAvatar={!!thread.directRecipient}
          showEmailButton={
            projectEmailEnabled &&
            hasPermission &&
            !thread.isSubthread &&
            !thread.directRecipient
          }
          showChatButton={true}
          projectKey={projectKey}
          unreadEmailCount={unreadEmailCount}
          unreadChatCount={thread.unreadMessagesCount}
          onEmailButtonClick={onClose}
          onChatClick={handleChatButtonClick}
        />
      );
    },
    [allUsers, handleChatButtonClick, onClose]
  );

  return (
    <div className={classes.root}>
      <div className={classes.drawerHeader}>
        <Typography className={classes.drawerTitle}>
          {t('navigation.message_center')}
        </Typography>
        <IconButton
          className={classes.drawerCloseButton}
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <Icon path={mdiClose} size={0.9} />
        </IconButton>
      </div>

      <div className={classes.searchContainer}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextInput
              id="chat-menu-search"
              fullWidth
              label={t('common.search')}
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
            />
          </Grid>
        </Grid>
      </div>
      <div className={classes.listContainer}>
        {!searchQuery && hiddenOlderUnreadCount > 0 && (
          <div className={classes.olderUnread}>
            <Typography className={classes.olderUnreadText}>
              {t('chat.older_unread_hidden', {
                count: hiddenOlderUnreadCount,
              })}
            </Typography>
            <Button
              className={classes.olderUnreadButton}
              size="small"
              onClick={() => setShowOlderUnread((shown) => !shown)}
            >
              {showOlderUnread
                ? t('chat.hide_older_unread')
                : t('chat.show_older_unread')}
            </Button>
          </div>
        )}
        {Object.keys(threadsByProject).length === 0 &&
        filteredEmployerProjects.length === 0 ? (
          <div className={classes.noMessages}>
            {searchQuery ? t('chat.no_results') : t('chat.no_new_messages')}
            {!searchQuery && (
              <div className={classes.noMessagesHint}>
                {t('chat.search_older_conversations')}
              </div>
            )}
          </div>
        ) : (
          sortedProjectIds.map((projectId, index) => {
            const project = projects[projectId];
            const projectName = project?.name || projectId;
            const threads = threadsByProject[projectId] || [];
            const projectKey = project?.projectKey || '';
            const unreadEmailCount = Math.max(
              0,
              Number(unreadMessages[projectKey]) || 0
            );
            const projectEmailEnabled = isFeatureEnabled(
              Feature.PROJECT_EMAIL,
              project
            );
            const hasPermission = projectEmailPermissions[projectId];
            return (
              <React.Fragment key={projectId}>
                {index > 0 && <Divider className={classes.divider} />}
                <Typography className={classes.projectHeader}>
                  {projectName}
                </Typography>
                {filteredEmployerProjects.find(
                  (empProject) => empProject.id === projectId
                ) && (
                  <ChatProjectListItem
                    title={projectName}
                    subtitle={projectKey}
                    lastMessageAt={emailLastMessageAt[projectKey]}
                    showEmailButton={true}
                    showChatButton={false}
                    projectKey={projectKey}
                    unreadEmailCount={unreadEmailCount}
                    onEmailButtonClick={onClose}
                  />
                )}
                {threads.map((thread) =>
                  renderThreadItem(
                    thread,
                    projectKey,
                    projectName,
                    projectEmailEnabled,
                    hasPermission,
                    unreadEmailCount
                  )
                )}
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  );
};

ChatMenuContent.propTypes = {
  onClose: PropTypes.func,
};

export default ChatMenuContent;
